import { Component, inject } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { EMPTY } from "rxjs";
import { catchError, finalize } from "rxjs/operators";
import { CstDialogResult } from "src/app/common-components/dialogs/cst-dialog-enums";
import { CstDialogsService } from "src/app/common-components/dialogs/cst-dialogs.service";
import { FormControlBase } from "src/app/common-components/form-controls/form-control-base";
import { CstResponsivePopupComponentBase } from "src/app/common-components/resp-popup";
import { CstResponsivePopupService } from "src/app/common-components/resp-popup/services/resp-popup.service";
import {
  AnlageDetailDto,
  AnlagenClientProvider,
  FacilityCreateCommand,
  FacilityNoType,
  LKZLookupDto,
  StandortDetailDto,
  ValidationResult,
} from "src/app/web-api/web-api";
import { AnlageApiFacilityLocationSelectDialogComponent } from "../facility-location-select/facility-location-select.component";

@Component({
  templateUrl: "./facility-create.component.html",
  standalone: false,
})
export class AnlageApiFaciliyCreateDialogComponent extends CstResponsivePopupComponentBase<
  any,
  AnlageDetailDto
> {
  private _client = inject(AnlagenClientProvider);
  private _popupService = inject(CstResponsivePopupService);
  private _dlgs = inject(CstDialogsService);

  private _formChangeRecursion = false;

  public FacilityNoType = FacilityNoType;
  public form: FormGroup = null;
  public loading = false;

  public get ctrlFacilityNoInternalCustom() {
    return this.form.controls.facilityNoInternalCustom as FormControl;
  }
  public get grpLocation() {
    return this.form.controls.location as FormGroup;
  }

  public constructor() {
    const fb = inject(FormBuilder);

    super();
    this.form = fb.group({
      locationID: [null],
      location: fb.group({
        name: [null, Validators.maxLength(200)],
        street: [null, Validators.maxLength(126)],
        streetNo: [null, Validators.maxLength(10)],
        city: [null, Validators.maxLength(126)],
        postalCode: [null, Validators.maxLength(10)],
        // TODO: Auswahl des Landes aus Wawi LKZ Tabelle
        country: ["Deutschland", Validators.maxLength(126)],
        countryID: [52],
      }),
      facilityNoInternalCustom: [
        { value: null, disabled: true },
        Validators.compose([Validators.required, Validators.maxLength(41)]),
      ],
      sourceOfFacilityNoInternal: [FacilityNoType.Own],
      facilityNoExternal: [null, Validators.maxLength(41)],
      label: [
        null,
        Validators.compose([Validators.required, Validators.maxLength(63)]),
      ],
      buildingTypeID: [null],
      contactEmail: [
        null,
        Validators.compose([Validators.email, Validators.maxLength(200)]),
      ],
      appointmentRequired: [false],
    });

    this.form.valueChanges.subscribe(() => {
      if (this._formChangeRecursion) {
        return;
      }
      this._formChangeRecursion = true;
      const cmd = new FacilityCreateCommand();
      cmd.init(this.form.value);
      if (cmd.sourceOfFacilityNoInternal === FacilityNoType.Custom) {
        this.ctrlFacilityNoInternalCustom.enable();
      } else {
        this.ctrlFacilityNoInternalCustom.disable();
      }
      if (cmd.locationID) {
        this.grpLocation.disable();
      } else {
        this.grpLocation.enable();
      }
      this.form.updateValueAndValidity();
      this._formChangeRecursion = false;
    });
  }

  public canAccept() {
    const result = !this.loading && this.form.valid;
    return result;
  }

  public onAccept() {
    if (!this.canAccept()) {
      return;
    }
    const cmd = new FacilityCreateCommand();
    cmd.init(this.form.value);
    this.loading = true;
    this._client
      .anlageCreate(cmd)
      .pipe(
        catchError((error) => {
          if (error instanceof ValidationResult) {
            setTimeout(() => {
              FormControlBase.ApplyValidationResultToForm(this.form, error);
              FormControlBase.ApplyValidationResultToForm(
                this.grpLocation,
                error,
              );
            }, 1);
            return EMPTY;
          }
          console.log(error);
          return EMPTY;
        }),
        finalize(() => (this.loading = false)),
      )
      .subscribe((result) => {
        this.close(result);
      });
  }

  public onCancel() {
    this.close();
  }

  public onFacilityLocationSelect() {
    const ref = this._popupService.open<
      AnlageApiFacilityLocationSelectDialogComponent,
      any,
      StandortDetailDto
    >(AnlageApiFacilityLocationSelectDialogComponent);
    ref.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }
      this._dlgs
        .AskYesCancel(
          // eslint-disable-next-line max-len
          $localize`:@@AnlageAPI_FacilityCreate_Prompt_ChooseLocation_Text:Use this location? \n\n ${result?.street ?? ""} ${result?.streetNo ?? ""}, ${result?.postalCode ?? ""} ${result?.city ?? ""}`,
          // ...existing code...,
          undefined,
          $localize`:@@AnlageAPI_FacilityCreate_Prompt_ChooseLocation_ButtonCopy:Accept`,
        )
        .subscribe((dlgResult) => {
          if (dlgResult === CstDialogResult.Cancel) {
            return;
          } else if (dlgResult === CstDialogResult.Yes) {
            this.form.patchValue({ locationID: null, location: result });
          }
        });
    });
  }

  public onCountrySelected(item: LKZLookupDto) {
    this.form.patchValue({
      location: {
        country: item.land,
      },
    });
  }

  public getHintForSourceOfFacilityNoInternal() {
    if (this.form.value.sourceOfFacilityNoInternal === FacilityNoType.Custom) {
      return "";
    }
    return $localize`:@@AnlageAPI_FacilityCreate_FacilityNoInternal_Hint:Number will be drawn on the server`;
  }
}
